import { md5 } from 'js-md5'
import { ROUTES } from '@/router/routeNames'
import type { RouteLocationRaw } from 'vue-router'
import { formatRelativeDate } from '@/services/date'

/**
 * Visitor type with profile information
 */
export interface Visitor {
  visitorId: string
  profile?: {
    emailAddress?: string
    [key: string]: any
  }
  [key: string]: any
}

/**
 * Generate avatar URL for a visitor
 * Uses pre-assigned avatarUrl if provided (from backend), otherwise falls back to Gravatar
 * @param email - Email address to generate gravatar for
 * @param avatarUrl - Optional pre-assigned avatar URL (provided by backend for demo mode)
 * @returns Avatar URL or undefined if no email
 */
export const getGravatarUrl = (email: string | undefined, avatarUrl?: string | null): string | undefined => {
  // If backend provided an avatarUrl, use it directly (handles demo mode)
  if (avatarUrl) {
    return avatarUrl
  }

  if (!email) return undefined

  // Fall back to Gravatar for real users
  const hash = md5(email.toLowerCase().trim())
  return `https://www.gravatar.com/avatar/${hash}?s=200&d=mp`
}

/**
 * Get initials for a visitor
 * @param email - Email address to extract initials from
 * @returns Two-character initials or 'AN' for anonymous
 */
export const getVisitorInitials = (email: string | undefined): string => {
  if (email) {
    return email.substring(0, 2).toUpperCase()
  }
  return 'AN'
}

/**
 * Get display label for a visitor
 * @param visitor - Visitor object with optional profile
 * @returns Email address or 'Anonymous Visitor'
 */
export const getVisitorLabel = (visitor: Visitor | null | undefined): string => {
  if (!visitor) return 'Anonymous Visitor'
  return visitor.profile?.emailAddress || 'Anonymous Visitor'
}

/**
 * Get route object for visitor detail page
 * @param visitor - Visitor object
 * @returns Vue Router route location object
 */
export const getVisitorDetailRoute = (visitor: Visitor): RouteLocationRaw => {
  // Vue Router encodes named params when generating the URL.
  const identifier = visitor.profile?.emailAddress || visitor.visitorId

  return {
    name: ROUTES.VISITOR_DETAIL,
    params: { visitorId: identifier }
  }
}

/**
 * Check if a visitor is identified (has an email address)
 * @param visitor - Visitor object
 * @returns true if visitor has an email address
 */
export const isIdentifiedVisitor = (visitor: Visitor): boolean => {
  return !!visitor.profile?.emailAddress
}

/**
 * Sort visitors by identification status and recency
 * Identified visitors first, then sorted by most recent
 * @param visitors - Array of visitors to sort
 * @returns Sorted array of visitors
 */
export const sortVisitorsByRecency = (visitors: Visitor[]): Visitor[] => {
  return [...visitors].sort((a, b) => {
    const aHasEmail = isIdentifiedVisitor(a)
    const bHasEmail = isIdentifiedVisitor(b)

    // Identified visitors first
    if (aHasEmail && !bHasEmail) return -1
    if (!aHasEmail && bHasEmail) return 1

    // Then sort by most recent
    const aTime = new Date(a.lastSeen || 0).getTime()
    const bTime = new Date(b.lastSeen || 0).getTime()
    return bTime - aTime
  })
}

/**
 * Check if we should show "First seen" (only if > 24 hours between first and last)
 * @param firstSeen - First seen timestamp
 * @param lastSeen - Last seen timestamp
 * @returns true if first seen should be displayed
 */
export const shouldShowFirstSeen = (firstSeen: string | undefined, lastSeen: string | undefined): boolean => {
  if (!firstSeen || !lastSeen) return false

  const firstTime = new Date(firstSeen).getTime()
  const lastTime = new Date(lastSeen).getTime()
  const hoursDiff = (lastTime - firstTime) / (1000 * 60 * 60)

  return hoursDiff >= 24
}

/**
 * Format first seen - "X day(s) ago" if < 3 days, otherwise "Sep 23" or "Sep 23, 2024"
 * @param firstSeen - First seen timestamp
 * @returns Formatted string
 */
export const formatFirstSeen = (firstSeen: string | undefined): string => {
  if (!firstSeen) return ''

  const date = new Date(firstSeen)
  const now = new Date()
  const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  // If less than 3 days ago, show relative format
  if (daysDiff < 3) {
    if (daysDiff === 0) return 'today'
    if (daysDiff === 1) return '1 day ago'
    return `${daysDiff} days ago`
  }

  // Otherwise show date format
  const sameYear = date.getFullYear() === now.getFullYear()
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' })
  }

  return date.toLocaleDateString(undefined, options)
}

/**
 * Get the full "First seen X / Last seen Y" display string
 * @param firstSeen - First seen timestamp
 * @param lastSeen - Last seen timestamp
 * @returns Formatted display string
 */
export const getFirstLastSeenDisplay = (firstSeen: string | undefined, lastSeen: string | undefined): string => {
  if (!lastSeen) return ''

  const lastSeenText = `Last seen ${formatRelativeDate(lastSeen)}`

  if (shouldShowFirstSeen(firstSeen, lastSeen)) {
    return `First seen ${formatFirstSeen(firstSeen)} / ${lastSeenText}`
  }

  return lastSeenText
}
